import React, { useState, useEffect, useCallback, useMemo } from "react";

import { motion } from "framer-motion";

import { createGlobalStyle } from "styled-components";
import { useHistory, useLocation } from "react-router-dom";

import { useAtom } from "jotai";
import {
  themeAtom,
  isAdminAtom,
  isMobileBrowserAtom,
  exportDatesModalOpenAtom,
  isDockHiddenAtom,
  eventDataModalOpenAtom,
  emailSelectorDialogOpenAtom,
  isShootTabAtom,
  isElectronAtom,
} from "./-Atoms";

import { subscribe, unsubscribe } from "./GlobalFunctions/customEvent";

import { saveAs } from "file-saver";
import {
  Calendar,
  KanbanSquare,
  PlayCircle,
  Users,
  User,
  Globe,
  Share,
  Mail,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
} from "lucide-react";

// import { useMediaQuery } from "@mui/material";
import { db, collection, getDocs } from "./firebaseConfig";

const BASE_PAGE_CONFIGS = [
  {
    name: "Calendar",
    icon: Calendar,
    restricted: true,
    admin: false,
    electronShow: true,
    routeBuilder: ({ hasAutologin }) =>
      hasAutologin ? "/calendar/autologin" : "/calendar",
  },
  {
    name: "Kanban",
    icon: KanbanSquare,
    restricted: true,
    admin: false,
    electronShow: true,
    routeBuilder: ({ hasAutologin }) =>
      hasAutologin ? "/kanban/autologin" : "/kanban",
  },
  {
    name: "Video Schedule",
    route: "/videoschedule",
    icon: PlayCircle,
    restricted: false,
    admin: false,
    electronShow: true,
  },
  {
    name: "Clients",
    route: "/clients",
    icon: Users,
    restricted: false,
    admin: true,
    electronShow: true,
  },
  {
    name: "Staff",
    route: "/staff",
    icon: User,
    restricted: false,
    electronShow: false,
    adminSelector: ({ isElectron }) => (isElectron ? true : false),
  },
  {
    name: "Outsourcers",
    route: "/outsourcers",
    icon: Globe,
    restricted: false,
    admin: true,
    electronShow: true,
  },
];

const MOBILE_EXTRA_PAGE_NAMES = [
  "Video Schedule",
  "Clients",
  "Staff",
  "Outsourcers",
];


const GlobalStyles = createGlobalStyle`
.toolTip {
  opacity: 0;
  transition: opacity 0.3s ease;
}
.visible {
  opacity: 1;
}
.iconSizeSmall {
  font-size: 25px;
  transition: font-size 0.2s ease;
}
.iconSizeLarge {
  font-size: 40px;
}
`;

const buildDockPages = (pathname, isElectron) => {
  const hasAutologin = pathname.includes("autologin");

  return BASE_PAGE_CONFIGS.map((config) => {
    const {
      routeBuilder,
      adminSelector,
      route: defaultRoute,
      ...rest
    } = config;

    const route = routeBuilder ? routeBuilder({ hasAutologin }) : defaultRoute;

    const admin = adminSelector ? adminSelector({ isElectron }) : rest.admin;

    return {
      ...rest,
      route,
      admin,
    };
  });
};

const Dock = ({ restricted }) => {
  const [theme] = useAtom(themeAtom);
  const [isElectron] = useAtom(isElectronAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [isMobileBrowser] = useAtom(isMobileBrowserAtom);
  const [eventDataModalOpen] = useAtom(eventDataModalOpenAtom);
  const [isShootTab] = useAtom(isShootTabAtom);
  const [isDockHidden, setIsDockHidden] = useAtom(isDockHiddenAtom);
  const [, setExportDatesModalOpen] = useAtom(exportDatesModalOpenAtom);
  const [, setEmailSelectorDialogOpen] = useAtom(emailSelectorDialogOpenAtom);

  const location = useLocation();
  const history = useHistory();

  const pages = useMemo(
    () => buildDockPages(location.pathname, isElectron),
    [location.pathname, isElectron]
  );

  const extrasSubmenu = useMemo(() => {
    if (!isMobileBrowser) return [];

    return pages
      .filter((page) => MOBILE_EXTRA_PAGE_NAMES.includes(page.name))
      .filter((page) =>
        shouldReturnPage(page, isAdmin, restricted, isElectron)
      )
      .map((page) => ({
        name: page.name,
        function: () => history.push(page.route),
      }));
  }, [
    isMobileBrowser,
    pages,
    isAdmin,
    restricted,
    isElectron,
    history,
  ]);

  const hideDock = useCallback(() => {
    setIsDockHidden(true);
  }, [setIsDockHidden]);

  const showDock = useCallback(() => {
    setIsDockHidden(false);
  }, [setIsDockHidden]);

  useEffect(() => {
    subscribe("showDock", showDock);
    subscribe("hideDock", hideDock);

    return () => {
      unsubscribe("showDock", showDock);
      unsubscribe("hideDock", hideDock);
    };
  }, [showDock, hideDock]);

  return (
    <div>
      <GlobalStyles />
      <DockHandle
        theme={theme}
        isDockHidden={isDockHidden}
        isMobileBrowser={isMobileBrowser}
        onShow={showDock}
      />
      <DockPanel
        theme={theme}
        isDockHidden={isDockHidden}
        isMobileBrowser={isMobileBrowser}
      >
        {pages &&
          pages.map((page, index) => {
            if (!shouldReturnPage(page, isAdmin, restricted, isElectron))
              return null;
            if (
              isMobileBrowser &&
              MOBILE_EXTRA_PAGE_NAMES.includes(page.name)
            )
              return null;

            return (
              <AppIconButton
                key={page.name || index}
                button={{
                  name: page.name,
                  icon: page.icon,
                  tooltip: page.name,
                  function: () => history.push(page.route),
                  showActiveDot: shouldShowPageDot(
                    location.pathname,
                    page.route
                  ),
                }}
              />
            );
          })}
        {isMobileBrowser && extrasSubmenu.length > 0 ? (
          <AppIconButton
            button={{
              name: "Extras",
              icon: MoreHorizontal,
              tooltip: "Extras",
              submenu: extrasSubmenu,
            }}
          />
        ) : null}
        <DockDivider theme={theme} />
        {shouldShowExportDates(location, isAdmin) &&
        !eventDataModalOpen &&
        !isMobileBrowser ? (
          <AppIconButton
            button={{
              name: `Export`,
              admin: true,
              function: () => {},
              icon: Share,
              submenu: [
                {
                  name: "Export Dates",
                  function: () => {
                    setExportDatesModalOpen(true);
                    hideDock();
                  },
                },
                {
                  name: "Export Contacts CSV",
                  function: async () => {
                    const contactsRef = collection(db, "ClientDatabase");
                    const querySnapshot = await getDocs(contactsRef);
                    console.log(querySnapshot.docs);
                    let contactsArray = [];

                    querySnapshot.docs.forEach((doc) => {
                      const contacts = doc.data().contacts;
                      contacts &&
                        contacts.forEach((contact) => {
                          contactsArray.push({
                            email: contact.email.toLowerCase(),
                            name: contact.name,
                          });
                        });
                    });

                    const uniqueContacts = [];
                    const emailsSet = new Set();

                    contactsArray.forEach((contact) => {
                      if (!emailsSet.has(contact.email)) {
                        emailsSet.add(contact.email);
                        uniqueContacts.push(contact);
                      }
                    });

                    const csvContent = uniqueContacts
                      .map((contact) => `${contact.email},${contact.name}`)
                      .join("\n");

                    const csvHeaders = "Email,Name\n";
                    const csvData = csvHeaders + csvContent;

                    const blob = new Blob([csvData], {
                      type: "text/csv;charset=utf-8;",
                    });
                    saveAs(blob, "contacts.csv");
                  },
                },
              ],
            }}
          />
        ) : null}
        {isShootTab ? (
          <AppIconButton
            button={{
              name: `Emails`,
              admin: true,
              function: () => {
                setEmailSelectorDialogOpen(true);
                hideDock();
              },
              icon: Mail,
            }}
          />
        ) : null}

        <AppIconButton
          button={{
            name: "Hide",
            icon: ChevronDown,
            function: () => {
              hideDock();
            },
          }}
        />
      </DockPanel>
    </div>
  );
};
// page.admin && isAdmin ? (
//   <AppIcon key={index} page={page} />
// ) : !page.admin &&
//   returnRestricted(restricted, page.restricted) ? (
//   <AppIcon key={index} page={page} />
// ) : null
// )}
const DockHandle = ({ theme, isDockHidden, onShow, isMobileBrowser }) => (
  <div
    style={{
      position: "fixed",
      bottom: isMobileBrowser ? 20 : 20,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 9999990,
    }}
  >
    <motion.div
      style={{
        cursor: "pointer",
        background:
          theme === "dark"
            ? "rgba(60, 60, 60, 0.9)"
            : "rgba(200, 200, 200, 0.85)",
        width: 100,
        height: 22,
        borderRadius: 6,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      initial={{ y: 150 }}
      animate={{ y: isDockHidden ? 0 : 150 }}
      transition={{ type: "spring", damping: 13, stiffness: 200 }}
      onClick={onShow}
    >
      <ChevronUp
        size={18}
        color={theme === "dark" ? "#e5e5e5" : "#1c1c1c"}
        strokeWidth={2.5}
      />
    </motion.div>
  </div>
);

const DockPanel = ({ children, theme, isDockHidden, isMobileBrowser }) => (
  <div
    style={{
      position: "fixed",
      left: "50%",
      transform: "translateX(-50%)",
      bottom: isMobileBrowser ? 20 : 20,
      zIndex: 999999,
      pointerEvents: isDockHidden ? "none" : "all",
    }}
  >
    <motion.div
      className={`mx-auto flex ${
        isMobileBrowser ? "h-14 gap-1.5" : "h-16 gap-4"
      } items-center px-3.5 pb-1`}
      style={{
        backgroundColor:
          theme === "dark"
            ? "rgba(10, 10, 10, 0.2)"
            : "rgba(220, 220, 220, 0.4)",
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)",
        borderRadius: 10,
      }}
      initial={{ y: 150 }}
      animate={{ y: isDockHidden ? 150 : 0 }}
      transition={{ type: "spring", damping: 13, stiffness: 200 }}
    >
      {children}
    </motion.div>
  </div>
);

const DockDivider = ({ theme }) => (
  <div
    style={{
      height: "80%",
      borderRight: `2px solid ${
        theme === "dark" ? "rgba(240, 240, 240, 0.8)" : "rgba(30, 30, 30, 0.8)"
      }`,
      maskImage:
        "linear-gradient(0deg, transparent, rgb(255, 255, 255) 16px, rgb(255, 255, 255) calc(100% - 16px), transparent)",
    }}
  >
    &zwnj;
  </div>
);



export default Dock;

function shouldShowExportDates(location, isAdmin) {
  if (!isAdmin) return false;
  if (location.pathname.includes("calendar")) return true;
  if (location.pathname === "/") return true;

  return false;
}

function shouldReturnPage(page, isAdmin, restricted, isElectron) {
  if (isElectron) {
    return page.electronShow;
  }

  if (isAdmin) return true;

  if (restricted) {
    return !!page.restricted;
  }

  return !page.admin;
}

function AppIconButton({ button }) {
  const [theme] = useAtom(themeAtom);
  const [isMobileBrowser] = useAtom(isMobileBrowserAtom);

  const [isHovered, setIsHovered] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [hoveredSubmenuIndex, setHoveredSubmenuIndex] = useState(null);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleSubmenuClick = (submenuItem) => {
    setSubmenuOpen(false);
    submenuItem.function();
  };

  const IconComponent = button.icon;
  const iconSize = isMobileBrowser ? 38 : 48;
  const showSubmenu = submenuOpen && button.submenu;
  const tooltipText = button.tooltip ?? button.name;
  const showTooltip = Boolean(tooltipText);

  const handleClick = () => {
    if (button.submenu) {
      setSubmenuOpen((prev) => !prev);
      return;
    }

    button.function && button.function();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
      }}
    >
      {showTooltip ? (
        <div
          style={{
            position: "absolute",
            top: -52,
            backgroundColor:
              theme === "dark"
                ? "rgba(30, 30, 30, 0.8)"
                : "rgba(240, 240, 240, 0.8)",
            borderRadius: 8,
            padding: "5px 8px",
            fontSize: 14,
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
          className={`toolTip ${isHovered ? "visible" : ""}`}
        >
          {tooltipText}
        </div>
      ) : null}
      <div
        style={{
          height: 4,
          width: 4,
          borderRadius: 2,
          backgroundColor: isHovered
            ? theme === "dark"
              ? "rgba(10, 10, 10, 0.1)"
              : "rgba(255, 255, 255, 0.1)"
            : "transparent",
        }}
      >
        &zwnj;
      </div>
      <div
        style={{
          width: iconSize,
          height: iconSize,
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor:
            theme === "dark"
              ? "rgba(200, 200, 200, 0.85)"
              : "rgba(40, 40, 40, 0.85)",
          borderRadius: iconSize * 0.35,
          transition: "background-color 0.2s ease, transform 0.2s ease",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {IconComponent ? (
          <IconComponent
            color={theme === "dark" ? "#1c1c1c" : "#e5e5e5"}
            strokeWidth={2.5}
            style={{ width: "60%", height: "60%" }}
          />
        ) : null}
      </div>
      {showSubmenu ? (
        <div
          style={{
            position: "absolute",
            bottom: 80,
            backgroundColor:
              theme === "dark"
                ? "rgba(30, 30, 30, 0.95)"
                : "rgba(240, 240, 240, 0.95)",
            borderRadius: 12,
            padding: 8,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            boxShadow: `0 4px 12px ${
              theme === "dark" ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.1)"
            }`,
            zIndex: 1000,
            minWidth: 200,
          }}
        >
          {button.submenu.map((submenuItem, index) => {
            const isItemHovered = hoveredSubmenuIndex === index;
            return (
              <div
                key={index}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderRadius: 6,
                  fontSize: 14,
                  backgroundColor: isItemHovered
                    ? theme === "dark"
                      ? "rgba(255, 255, 255, 0.07)"
                      : "rgba(0, 0, 0, 0.07)"
                    : "transparent",
                  transition: "background-color 0.15s ease",
                }}
                onMouseEnter={() => setHoveredSubmenuIndex(index)}
                onMouseLeave={() => setHoveredSubmenuIndex(null)}
                onClick={() => handleSubmenuClick(submenuItem)}
              >
                {submenuItem.name}
              </div>
            );
          })}
        </div>
      ) : null}
      {button.showActiveDot ? (
        <div
          style={{
            position: "absolute",
            bottom: 3,
            backgroundColor: theme === "dark" ? "#1c1c1c" : "#e5e5e5",
            borderRadius: 25,
            width: 3.5,
            height: 3.5,
          }}
        ></div>
      ) : null}
    </div>
  );
}

function shouldShowPageDot(pathName, route) {
  if (pathName.includes(route)) return true;
  if (pathName === "/" && route.includes("calendar")) return true;
  return false;
}
